import {  Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, last, map } from 'rxjs/operators';
import { BaseService } from './base.service';
import { ContentService } from './content.service';
import { DatalayerService } from './datalayer.service';
import { HomeService } from './home.service';
import { SettingsService } from './settings.service';
import { StorageService } from './storage.service';
import { WishlistService } from './wishlist.service';
import {environment}  from '../../../environment'
import npaw from 'npawlib';
import { youboraAdapaterCode } from 'src/app/shared/constants';
import { InhouseAnalyticsService } from './inhouse-analytics.service';

declare global {
  interface Window {
    plugin?: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PlayContentService {
  categoryName: any;
  searchData: any;
  pageDetail: string;
  scrollTopValue: number;
  removeList = false;
  progressBarPercentage = new BehaviorSubject<number>(0);
  epgPlayContent = new BehaviorSubject<any>({});
  epgNextContent = new BehaviorSubject<string>('');
  playEnded = new BehaviorSubject<string>('');
  showAudioPlayer = new BehaviorSubject<boolean>(false);
  audioPlayerWishListData = new BehaviorSubject<object>({});
  audioContentObject = new BehaviorSubject<object>({});
  currentContentCollection = new BehaviorSubject<object>({});
  selectedContentType = new BehaviorSubject<string>('audio');
  closeMiniPlayer = new BehaviorSubject<boolean>(false);
  channelNotAvailable = false;
  firstInitInSmartChannel = false;
  smartChannelData;
  ChannelWithAD = false;
  uId;
  appAccessType;
  cmpEnabled;
  gdpr = 0;
  gdprConsent;
  usprivacyValue;
  hasedDeviceId;
  appName: string;

  constructor(private router: Router, private contentService: ContentService,
              private storageService: StorageService, private datalayerService: DatalayerService,
              private settingsService: SettingsService, private wishListService: WishlistService,
              private homeService: HomeService, private baseService: BaseService, private analyticsService: InhouseAnalyticsService) {
                this.cmpEnabled = this.settingsService.cmpEnabled;
                this.appName = environment.apiAppName;
               }

  setYouboraDebug() {
    npaw.Log.logLevel = npaw.Log.Level.VERBOSE;
  }

  async getPlaybackUrl(playbackUrl, data) {
    let url = playbackUrl;
    if (this.cmpEnabled) {
      try {
          const consentData = await this.getCmpConsentData();
          if (consentData) {
            if (consentData.gdprApplies) this.gdpr = 1;
            this.gdprConsent = consentData.tcString;
            this.usprivacyValue = consentData.usPolicy;

            let replacements = {
                '{{CONTENT_ID}}': data?.content_id || data?.customer_content_id,
                '{{CONTENT_TITLE}}': data?.content_title || data?.title,
            };
            if (data?.genre) {
              replacements['{{CONTENT_GENRE}}'] = this.encodeValueIfSpecialChars(data?.genre);
            }

            for (const [key, value] of Object.entries(replacements)) {
                if (url.includes(key)) {
                    url = url.replace(key, value);
                } else {
                    url += `&${key.replace(/[{}]/g, '').toLowerCase()}=${value}`;
                }
            }

            const d_id = this.storageService.getLocalStore('d_id');
            const hasedDeviceId = await this.getHashDeviceId(d_id);
            const userAgent = decodeURIComponent(navigator.userAgent);
            url += `&did=${hasedDeviceId}&ua=${userAgent}&us_privacy=${this.usprivacyValue}&gdpr=${this.gdpr}&gdpr_consent=${this.gdprConsent}&app_name=${this.appName}&dnt=0&coppa=0`;
            return url;
          } else {
            return url;
          }
      } catch (error) {
          console.error(error);
          return url;
      }
    } else {
        return url;
    }
}

  encodeValueIfSpecialChars(value: string): string {
    if (/[^a-zA-Z0-9]/.test(value)) {
      return encodeURIComponent(value);
    }
    return value;
  }

  async getHashDeviceId(deviceId: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(deviceId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  getCmpConsentData(): Promise<any> {
    return new Promise((resolve, reject) => {
      const consentData: any = {};

      // Check for GDPR consent data
      if (typeof window.__tcfapi !== 'undefined') {
        window.__tcfapi('getTCData', 2, (tcData, success) => {
          if (success) {
            consentData.gdprApplies = tcData.gdprApplies;
            consentData.tcString = tcData.tcString;

            // Check for US Privacy String
            if (typeof window.__uspapi !== 'undefined') {
              window.__uspapi('getUSPData', 1, (usPolicyData, success) => {
                if (success) {
                  consentData.usPolicy = usPolicyData.uspString;
                  resolve(consentData);
                } else {
                  reject('Failed to get US policy data');
                }
              });
            } else {
              resolve(consentData); // Resolve with GDPR data if USP API is not available
            }
          } else {
            reject('Failed to get GDPR consent data');
          }
        });
      } else {
        // Check for US Privacy String if GDPR API is not available
        if (typeof window.__uspapi !== 'undefined') {
          window.__uspapi('getUSPData', 1, (usPolicyData, success) => {
            if (success) {
              consentData.usPolicy = usPolicyData.uspString;
              resolve(consentData);
            } else {
              reject('Failed to get US policy data');
            }
          });
        } else {
          reject('Both TCF and USP APIs are not available');
        }
      }
    });
  }

  initializeYoubora(initoptions) {
    try {
      this.setYouboraDebug();
      const options = {...{ accountCode: this.settingsService.youboraAccountCode}, ...initoptions};
      window.plugin = new npaw.Plugin(options);
    } catch(err) {
      console.log("Error Initializing Youbora");
      console.log(err);
    }
  }

  youborainitOptions(content, isLive) {
    const initOptions = {
      'content.isLive': isLive,
      'content.streamingProtocol': this.getStreamingType(content?.playback_url) ?? '',
      'content.drm': content?.is_drm,
      'user.name' : this.storageService.getLocalStore('u_id') ?? '',
      'app.name': this.settingsService?.youboraSiteName,
      'app.releaseVersion': environment.appVersion,
      'content.title': content?.title_en
    }
    return initOptions;
  }

  addAdapterToPlayer(player) {
    try {
      const adapterCode = JSON.stringify(youboraAdapaterCode);
      window.plugin.registerAdapter(player, adapterCode);
    } catch(err) {
      console.log(err);
    }
  }

  sendYouboraFatalError() {
    try {
      window.plugin.getAdapter().fireFatalError();
      window.plugin.removeAdapter();
    } catch(err) {
      console.log(err);
    }
  }

  getStreamingType(playbackUrl) {
    if (playbackUrl.match(/.mpd/g)) {
      return 'DASH';
    } else if (playbackUrl.match(/.m3u8/g)) {
      return 'HLS';
    } else {
      return 'MP4';
    } 
  }

  setPluginOptions(content, isLive, audio, subtitle) {
    try {
      const options = {
        'user.name' : this.storageService.getLocalStore('u_id') ?? '',
        'content.title': content.title_en ?? content.content_title,
        'content.isLive': isLive,
        'content.id': content.contentId,
        'content.resource': content.playback_url,
        'content.language': audio ?? '',
        'content.subtitles': subtitle ?? '',
        'content.streamingProtocol': this.getStreamingType(content.playback_url) ?? '',
        'content.drm': content.is_drm,
        'content.customDimension.1': content.content_id ? content.content_id : content.channel_id,
        'content.customDimension.2': content.title,
        'app.name': this.settingsService?.youboraSiteName,
        'app.releaseVersion': environment.appVersion,
        'device.id': this.storageService.getLocalStore('d_id') ?? '',
        'device.type': environment.deviceType
      }
      if(content.series_video) {
        options['content.tvShow'] = content.series_title;
        options['content.season'] = content.season_title;
        options['content.episodeTitle'] = content.content_title;
      }
      window.plugin.setOptions(options);
    } catch(err) {
      console.log(err);
    }
  }

  playVideo(value, status, buttonType?) {
      const eventObj = {
        event_type: 'Event',
        event_name: buttonType,
        event_status: 'Success',
        content_id: value.content_id,
        content_type: value.is_series ? 'Series' : (value.is_livechannel ? 'Live' : (value.is_audio ? 'Audio' : 'Video'))
      };
      this.analyticsService.trackUserActions(eventObj);

      this.baseService.loaderSource.next(true);
      this.contentService.autoPlayVideo = true;
      if (status == 'play') {
        this.contentService.play = true;
      } else {
        this.contentService.start = true;
      }
      this.addGTMData(value);
      if (value.is_livechannel) {
        this.baseService.storeEPGValue(value.slug);
        this.router.navigate(['/live', value.slug]);
      } else if (value.is_series) {
        this.handleSeriesRedirect(value);
      } else {
        this.router.navigate(['/v', value.collection_slug, value.content_slug]);
      }
  }

  handleSeriesRedirect(video) {
    const seriesSlug = video?.series_slug;
    const seasonSlug = video?.seasons_slug || 'd';
    const contentSlug = video?.content_slug;
    const video_id = video?.video_id;

    if (video.watched_duration) {
      this.contentService.contentSlug = contentSlug;
      this.contentService.seriesSlug = seriesSlug;
      this.contentService.seekValue = video.watched_duration;
    } else {
      this.contentService.seekValue = 0;
    }

    const toRoute = ['/vs', seriesSlug];
    if (video_id && contentSlug && seasonSlug) {
      toRoute.push(seasonSlug, contentSlug);
    }
    this.router.navigate(toRoute);
  }

  playTrailer(value) {
    const eventObj = {
      event_type: 'Event',
      event_name: 'Trailer Button Click',
      event_status: 'Success',
      content_id: value.content_id,
      content_type: value.is_series ? 'Series' : (value.is_livechannel ? 'Live' : (value.is_audio ? 'Audio' : 'Video'))
    };
    this.analyticsService.trackUserActions(eventObj);

    this.baseService.loaderSource.next(true);
    this.contentService.trailer = value;
    this.addGTMData(value);
    if (value.is_series) {
      const seriesSlug = value?.seasons_slug || value?.series_slug;
      const contentSlug = value?.content_slug;
      this.router.navigate(['/vs', seriesSlug]);
    } else {
      this.router.navigate(['/v', value.collection_slug, value.content_slug]);
    }
  }


  addGTMData(video) {
    if (this.categoryName) {
      const datalayerData = {
        event: 'GAEvent',
        eventCategory: 'Content Click',
        eventAction: this.categoryName,
        eventLabel: video?.title,
        loginStatus: (this.storageService.getLocalStore('u_id') ? 'True' : 'False'),
        userId: (this.storageService.getLocalStore('u_id') ? this.storageService.getLocalStore('u_id') : ''),
        content: this.datalayerService.getCategoryDatalayer(video, 'content'),
        contentId: video?.contentId,
        VideoCategory: this.datalayerService.getCategoryDatalayer(video, 'category'),
        notificationStatus: localStorage.getItem('emailNotify') ||  'False',
        region: (this.storageService.getLocalStore('region') ? this.storageService.getLocalStore('region') : ''),
        platform: this.baseService.browserType,
        userType: this.storageService.getLocalStore('user_type'),
        operator: this.storageService.getLocalStore('operator_name')
      };
      this.datalayerService.addDatalayer(datalayerData);
    }
    else if (this.searchData) {
      const datalayerData = {
        event: 'GAEvent',
        eventCategory: 'Search',
        eventAction: this.searchData,
        eventLabel: this.settingsService.virtualUrl,
        loginStatus: (this.storageService.getLocalStore('u_id') ? 'True' : 'False'),
        userId: (this.storageService.getLocalStore('u_id') ? this.storageService.getLocalStore('u_id') : ''),
        content: this.datalayerService.getCategoryDatalayer(video, 'content'),
        contentId: video?.contentId,
        VideoCategory: this.datalayerService.getCategoryDatalayer(video, 'category'),
        notificationStatus: localStorage.getItem('emailNotify') ||  'False',
        region: (this.storageService.getLocalStore('region') ? this.storageService.getLocalStore('region') : ''),
        platform: this.baseService.browserType,
        userType: this.storageService.getLocalStore('user_type'),
        operator: this.storageService.getLocalStore('operator_name')
      };
      this.datalayerService.addDatalayer(datalayerData);
    }
    else {
      const datalayerData = {
        event: 'GAEvent',
        eventCategory: 'Content Click',
        eventAction: (video.collection_title) ? video.collection_title : video?.seasons_title,
        eventLabel: video?.title,
        contentId: video?.contentId,
        loginStatus: (this.storageService.getLocalStore('u_id') ? 'True' : 'False'),
        userId: (this.storageService.getLocalStore('u_id') ? this.storageService.getLocalStore('u_id') : ''),
        notificationStatus: localStorage.getItem('emailNotify') ||  'False',
        region: (this.storageService.getLocalStore('region') ? this.storageService.getLocalStore('region') : ''),
        content: this.datalayerService.getCategoryDatalayer(video, 'content'),
        VideoCategory: this.datalayerService.getCategoryDatalayer(video, 'category'),
        platform: this.baseService.browserType,
        userType: this.storageService.getLocalStore('user_type'),
        operator: this.storageService.getLocalStore('operator_name')
      };
      this.datalayerService.addDatalayer(datalayerData);
    }
  }

  redirect(video, key) {
    const eventObj = {
      event_type: 'Event',
      event_name: 'More Details',
      event_status: 'Success',
      content_id: video.content_id,
      content_type: video.is_series ? 'Series' : (video.is_livechannel ? 'Live' : (video.is_audio ? 'Audio' : 'Video'))
    };
    this.analyticsService.trackUserActions(eventObj);

    this.addGTMData(video);
    this.baseService.loaderSource.next(true);
    if (video?.is_livechannel) {
      this.baseService.storeEPGValue(video.slug);
      this.router.navigate(['/live', video.slug]);
    } else if (video?.is_series) {
      const seriesSlug = video?.seasons_slug || video?.series_slug;
      const contentSlug = video?.content_slug;
      if (video.watched_duration) {
        this.contentService.contentSlug = contentSlug;
        this.contentService.seriesSlug = seriesSlug;
        this.contentService.seekValue = video.watched_duration;
      } else {
        this.contentService.seekValue = 0;
      }
      this.router.navigate(['/vs', seriesSlug]);
    } else if (key === 'video' && this.pageDetail === 'live') {
      this.router.navigate(['/live/player', 'video', video.content_id]);
    } else {
      this.router.navigate(['/v', video?.collection_slug, video?.content_slug]);
    }
  }

  removeWishlist(content) {
    const contentType = content.is_series && content.is_audio ? 'podcast' : content.is_series && !content.is_audio ? 'series' :!content.is_series && content.is_audio ? 'audio' :'video';
    const paramsData: object = {
      req: { content_type: contentType, id: content.content_id },
    };
    const removeMethod = content.is_series && !content.is_audio? this.wishListService.removeSeriesWishList(paramsData) : this.wishListService.removeWishlist(paramsData);
    removeMethod.subscribe(
      (data) => {
        const eventObj = {
          event_type: 'Event',
          event_name: 'Remove From Wishlist',
          event_status: 'Success',
        };
        this.analyticsService.trackUserActions(eventObj);

        if (data) {
          this.homeService.updateUserContentFlag = true;
          this.homeService.updateUserContent.next(true);
          if (content.is_series) {
            this.wishListService.removeFlagOn = true;
          }
        }
      }, err => {
        const eventObj = {
          event_type: 'Event',
          event_name: 'Remove From Wishlist',
          event_status: 'Failure',
          event_description: err
        };
        this.analyticsService.trackUserActions(eventObj);
      }
    );
  }
  

  addToWishlist(video) {
    const contentType = video.is_series && video.is_audio ? 'podcast' : video.is_series && !video.is_audio ? 'series' :!video.is_series && video.is_audio ? 'audio' :'video';
    const addWishlistData = { content_id: video.content_id, content_type: contentType, version:'v1' };
    this.wishListService.addToWishlist(addWishlistData).subscribe((data) => {
      const eventObj = {
        event_type: 'Event',
        event_name: 'Add To Wishlist',
        event_status: 'Success',
      };
      this.analyticsService.trackUserActions(eventObj);
      if (data) {
        //this.scrollTopValue = this.baseService.getScrollHeightValue();
        this.homeService.updateUserContentFlag = true;
        this.homeService.updateUserContent.next(true);
        this.gtmAddWishlist(contentType, video.title, video.content_id);
      }
    },err => {
      const eventObj = {
        event_type: 'Event',
        event_name: 'Add To Wishlist',
        event_status: 'Failure',
      };
      this.analyticsService.trackUserActions(eventObj);
    }, () => {

    });
  }

  audioAddToWishlist(video): Observable<any> {
    const contentType = video.is_series && video.is_audio ? 'podcast' : 'audio';
    const addWishlistData = { content_id: video.content_id, content_type: contentType, version: 'v1' };
    return this.wishListService.addToWishlist(addWishlistData).pipe(
        map((response: any) => {
            if (response.status_code === 200) {
                const eventObj = {
                    event_type: 'Event',
                    event_name: 'Add To Wishlist',
                    event_status: 'Success',
                };
                this.analyticsService.trackUserActions(eventObj);
                this.homeService.updateUserContentFlag = true;
                this.homeService.updateUserContent.next(true);
                this.gtmAddWishlist(contentType, video.title, video.content_id);
                return response;
            }
        }),
        catchError((error) => {
            const eventObj = {
                event_type: 'Event',
                event_name: 'Add To Wishlist',
                event_status: 'Failure',
            };
            this.analyticsService.trackUserActions(eventObj);
            return throwError(error);
        })
    );
 }

  gtmAddWishlist(type, title, id) {
    const gtmType = type === 'series' ? 'Series' : 'Movies';
    const u_id = this.storageService.getLocalStore('u_id');
    const datalayerData = {
      event: 'GAEvent',
      eventCategory: 'Add Wishlist',
      contentId: type === 'video' ? id : '',
      content: `${gtmType}|` + title + '|' + this.storageService.getLocalStore('localeName'),
      VideoCategory: `VOD - ${gtmType} - Wishlist`,
      eventAction: title,
      eventLabel: '',
      loginStatus: (u_id ? 'True' : 'False'),
      userId: (u_id ? u_id : ''),
      notificationStatus: localStorage.getItem('emailNotify') ||  'False',
      region: (this.storageService.getLocalStore('region') ? this.storageService.getLocalStore('region') : ''),
      platform: this.baseService.browserType,
      userType: this.storageService.getLocalStore('user_type'),
      operator: this.storageService.getLocalStore('operator_name')
    };
    this.datalayerService.addDatalayer(datalayerData);
  }

  updateWishlistData() {
    const uId = this.storageService.getLocalStore('u_id');
    if (uId) {
      this.homeService.updateUserContent
        .subscribe(async (emitFlag) => {
          if (emitFlag && this.homeService.updateUserContentFlag) {
            this.homeService.updateUserContentFlag = false;
            this.homeService.userContent()
            .pipe(last())
            .subscribe(async(data) => {
              if (data) {
                const userData = await data;
                let wishListData = [];
                let audioWishListData = [];
                let articleWishListData = [];
                this.homeService.userData.next(userData);
                userData.forEach(personaData => {
                  if (personaData['key'] === "wishlist") {
                    wishListData = personaData['content_ids'] || [];
                    this.homeService.updateAudioVideoWishlistData('wishListData',personaData.list);
                  }
                  if(personaData['key'] === 'audio_wishlist'){
                    audioWishListData = personaData['content_ids'] || [];
                    this.homeService.updateAudioVideoWishlistData('audioWishListData',personaData.list);
                  }
                  if(personaData['key'] === 'article_wishlist'){
                    articleWishListData = personaData['content_ids'] || [];
                    this.homeService.updateAudioVideoWishlistData('articleWishListData',personaData.list);
                  }
                });
                let concatedWishListdata = [...wishListData, ...audioWishListData, ...articleWishListData];
                this.homeService.contentWishlist.next(concatedWishListdata);
                this.baseService.loaderSource.next(false);
              }
            },() =>{
              this.baseService.loaderSource.next(false);
            })
          }
        },() =>{
          this.baseService.loaderSource.next(false);
        });
    }
  }
  redirectEPGContent(program) {
    if(program?.is_series === 0) this.router.navigate(['/v', 'epg', program.content_slug]);
    else {
      const series_slug = (program.season_slug != '') ? program.season_slug : program.series_slug;
      this.router.navigate(['/vs', series_slug]);
    }
  }

  loginRoute(contentType, contentId){
    const eventObj = {
      event_type: 'Event',
      event_name: 'Login Button',
      event_status: 'Success',
      content_id: contentId,
      content_type: contentType
    };
    this.analyticsService.trackUserActions(eventObj);
    this.storageService.setLocalStore('contentURL', location.href);
    this.router.navigateByUrl('/auth/signin');
  }

}
